/**
 * Keyboard Shortcuts
 * Provides keyboard navigation and shortcuts throughout the app
 */

class KeyboardShortcuts {
    constructor() {
        this.shortcuts = new Map();
        this.init();
    }
    
    init() {
        this.registerDefaultShortcuts();
        this.attachListeners();
        this.createHelperUI();
    }
    
    registerDefaultShortcuts() {
        // Navigation shortcuts
        this.register('/', () => {
            const searchInput = document.querySelector('.search-input, input[type="search"]');
            if (searchInput) {
                searchInput.focus();
                return false; // Prevent default
            }
        }, 'Focus search');
        
        this.register('g h', () => {
            window.location.href = '/';
        }, 'Go to homepage');
        
        this.register('g d', () => {
            window.location.href = '/dashboard';
        }, 'Go to dashboard');
        
        this.register('g p', () => {
            window.location.href = '/pricing';
        }, 'Go to pricing');
        
        // UI shortcuts
        this.register('Escape', () => {
            // Close any open modals or menus
            document.querySelectorAll('.modal.show, .dropdown.show').forEach(el => {
                el.classList.remove('show');
            });
            
            // Close mobile menu if open
            const mobileMenu = document.querySelector('.mobile-panel.is-active');
            if (mobileMenu) {
                document.getElementById('menuBurger')?.click();
            }
        }, 'Close modals/menus');
        
        this.register('?', () => {
            this.toggleHelp();
        }, 'Show keyboard shortcuts');
        
        // Form shortcuts
        this.register('ctrl+enter, cmd+enter', () => {
            const activeForm = document.activeElement.closest('form');
            if (activeForm) {
                activeForm.submit();
            }
        }, 'Submit form');
        
        // Accessibility
        this.register('Tab', null, 'Navigate forward');
        this.register('Shift+Tab', null, 'Navigate backward');
        this.register('Enter', null, 'Activate element');
        this.register('Space', null, 'Toggle checkbox/button');
    }
    
    register(keys, handler, description) {
        const normalizedKeys = this.normalizeKeys(keys);
        normalizedKeys.forEach(key => {
            this.shortcuts.set(key, { handler, description, keys: key });
        });
    }
    
    normalizeKeys(keys) {
        return keys.split(',').map(k => k.trim().toLowerCase());
    }
    
    attachListeners() {
        let sequence = [];
        let sequenceTimer;
        
        document.addEventListener('keydown', (e) => {
            // Don't trigger in input fields unless it's a global shortcut
            const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);
            const isGlobalShortcut = e.ctrlKey || e.metaKey || e.key === 'Escape';
            
            if (isInput && !isGlobalShortcut) return;
            
            const key = this.getKeyString(e);
            sequence.push(key);
            
            // Clear sequence after delay
            clearTimeout(sequenceTimer);
            sequenceTimer = setTimeout(() => {
                sequence = [];
            }, 800);
            
            // Check for matching shortcut
            const sequenceString = sequence.join(' ');
            const shortcut = this.shortcuts.get(sequenceString);
            
            if (shortcut && shortcut.handler) {
                const preventDefault = shortcut.handler(e);
                if (preventDefault === false) {
                    e.preventDefault();
                }
                sequence = [];
            }
        });
    }
    
    getKeyString(e) {
        const parts = [];
        
        if (e.ctrlKey) parts.push('ctrl');
        if (e.altKey) parts.push('alt');
        if (e.shiftKey) parts.push('shift');
        if (e.metaKey) parts.push('cmd');
        
        let key = e.key.toLowerCase();
        
        // Normalize special keys
        const keyMap = {
            ' ': 'space',
            'arrowup': 'up',
            'arrowdown': 'down',
            'arrowleft': 'left',
            'arrowright': 'right'
        };
        
        key = keyMap[key] || key;
        
        if (!['control', 'alt', 'shift', 'meta'].includes(key)) {
            parts.push(key);
        }
        
        return parts.join('+');
    }
    
    createHelperUI() {
        // Create help modal
        const helpModal = document.createElement('div');
        helpModal.className = 'keyboard-help-modal';
        helpModal.innerHTML = `
            <div class="keyboard-help-content">
                <h3>Keyboard Shortcuts</h3>
                <button class="keyboard-help-close">&times;</button>
                <div class="keyboard-shortcuts-list">
                    ${this.getShortcutsList()}
                </div>
            </div>
        `;
        
        document.body.appendChild(helpModal);
        
        // Close button handler
        helpModal.querySelector('.keyboard-help-close').addEventListener('click', () => {
            this.toggleHelp();
        });
    }
    
    getShortcutsList() {
        const groups = {
            'Navigation': [],
            'UI Controls': [],
            'Forms': [],
            'Accessibility': []
        };
        
        this.shortcuts.forEach((shortcut, keys) => {
            if (!shortcut.description) return;
            
            let group = 'UI Controls';
            if (keys.includes('g ')) group = 'Navigation';
            else if (keys.includes('tab') || keys.includes('enter') || keys.includes('space')) group = 'Accessibility';
            else if (keys.includes('ctrl+enter') || keys.includes('cmd+enter')) group = 'Forms';
            
            groups[group].push(`
                <div class="shortcut-item">
                    <kbd>${keys.replace(/\+/g, ' + ')}</kbd>
                    <span>${shortcut.description}</span>
                </div>
            `);
        });
        
        return Object.entries(groups)
            .filter(([_, shortcuts]) => shortcuts.length > 0)
            .map(([group, shortcuts]) => `
                <div class="shortcut-group">
                    <h4>${group}</h4>
                    ${shortcuts.join('')}
                </div>
            `).join('');
    }
    
    toggleHelp() {
        const modal = document.querySelector('.keyboard-help-modal');
        if (modal) {
            modal.classList.toggle('show');
        }
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    window.keyboardShortcuts = new KeyboardShortcuts();
});

export default KeyboardShortcuts;